/**
 * Auth Helpers — Authentication utilities for the account system
 *
 * Provides:
 * - Password hashing (SHA-256, symbolic/local)
 * - Link code generation & lookup
 * - Session management helpers
 * - Login validation
 *
 * @moved from src/utils/authHelpers.ts (R19 — Fase 2 continuation)
 */

import { AccountsState, Account, isLearnerAccount } from '../types'
import {
  PASSWORD_MIN_LENGTH,
  SESSION_TIMEOUT_MS,
  LINK_CODE_MAX_RETRIES,
  DISPLAY_NAME_MAX_LENGTH,
  DISPLAY_NAME_MIN_LENGTH,
} from '../../config/constants'
import { generateRandomCode } from '../../utils/randomCode'

/**
 * Hash a password using SHA-256 (symbolic, client-side only).
 * NOT real security — just prevents plain-text storage in localStorage.
 *
 * // FASE3: server-side auth required
 * In Fase 3 (backend migration), this client-side hashing MUST be removed.
 * Passwords should be sent securely over HTTPS and hashed server-side using bcrypt/argon2.
 */
export async function hashPassword(password: string): Promise<string> {
  // crypto.subtle is only available in secure contexts (HTTPS / localhost).
  // Fall back to a simple FNV-1a-based hash for non-secure contexts.
  // FASE3: All client-side hashing will be removed — server-side bcrypt/argon2.
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const encoder = new TextEncoder()
      const data = encoder.encode(password)
      const hashBuffer = await crypto.subtle.digest('SHA-256', data)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
    } catch {
      // Fall through to fallback
    }
  }
  // Fallback: FNV-1a 64-bit (symbolic only — NOT cryptographic)
  let h1 = 0x811c9dc5 >>> 0
  let h2 = 0x811c9dc5 >>> 0
  for (let i = 0; i < password.length; i++) {
    const c = password.charCodeAt(i)
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0
    h2 = Math.imul(h2 ^ (c + i + 0x9e), 0x01000193) >>> 0
  }
  return (
    h1.toString(16).padStart(8, '0') +
    h2.toString(16).padStart(8, '0') +
    h1.toString(16).padStart(8, '0') +
    h2.toString(16).padStart(8, '0')
  )
}

/**
 * Verify a password against a stored hash.
 *
 * // FASE3: server-side auth required
 * In Fase 3, password verification MUST happen on the server.
 * The client will receive a session token (JWT) upon successful login.
 */
export async function verifyPassword(
  password: string,
  storedHash: string,
): Promise<boolean> {
  const hash = await hashPassword(password)
  return hash === storedHash
}

/**
 * Generate a unique 6-digit link code for a learner.
 * Used by teachers to let students join their class.
 * Delegates to shared generateRandomCode() (D-SCAN-2 D2-2).
 */
export function generateLinkCode(): string {
  return generateRandomCode(6)
}

/**
 * Find an account by link code.
 */
export function findAccountByLinkCode(
  accounts: AccountsState,
  linkCode: string,
): Account | null {
  const normalizedCode = linkCode.toUpperCase().trim()
  const allAccounts = Object.values(accounts.accountsById)
  return (
    allAccounts.find(
      (a) => isLearnerAccount(a) && a.linkCode === normalizedCode,
    ) || null
  )
}

/**
 * Find an account by username (for learner login).
 */
export function findAccountByUsername(
  accounts: AccountsState,
  username: string,
): Account | null {
  const normalizedUsername = username.toLowerCase().trim()
  const allAccounts = Object.values(accounts.accountsById)
  return (
    allAccounts.find(
      (a) =>
        isLearnerAccount(a) && a.username?.toLowerCase() === normalizedUsername,
    ) || null
  )
}

/**
 * Find an account by email (for parent/teacher/admin login).
 */
export function findAccountByEmail(
  accounts: AccountsState,
  email: string,
): Account | null {
  const normalizedEmail = email.toLowerCase().trim()
  const allAccounts = Object.values(accounts.accountsById)
  return (
    allAccounts.find((a) => a.email?.toLowerCase() === normalizedEmail) || null
  )
}

/**
 * Check if all accounts in state are demo accounts.
 */
export function isAllDemoAccounts(accounts: AccountsState): boolean {
  const allAccounts = Object.values(accounts.accountsById)
  if (allAccounts.length === 0) return false
  return allAccounts.every((a) => a.isDemo === true)
}

/**
 * Check if any production (non-demo) accounts exist.
 */
export function hasProductionAccounts(accounts: AccountsState): boolean {
  return Object.values(accounts.accountsById).some((a) => !a.isDemo)
}

/**
 * Generate a username suggestion from a display name.
 * E.g., "Djayden van Berg" → "djayden.vanberg"
 */
export function suggestUsername(displayName: string): string {
  return displayName
    .toLowerCase()
    .replace(/\s+/g, '.')
    .replace(/[^a-z0-9.]/g, '')
    .replace(/\.{2,}/g, '.')
    .replace(/^\.+|\.+$/g, '')
}

/**
 * Validate a username (3-20 chars, alphanumeric + dots).
 */
export function isValidUsername(username: string): boolean {
  return /^[a-z0-9.]{3,20}$/.test(username)
}

/**
 * Validate a password (min 4 chars for kids, min 6 for adults).
 */
export function isValidPassword(
  password: string,
  role: 'learner' | 'parent' | 'teacher' | 'admin' = 'learner',
): boolean {
  const minLength =
    role === 'learner'
      ? PASSWORD_MIN_LENGTH.learner
      : PASSWORD_MIN_LENGTH.default
  return password.length >= minLength
}

/**
 * Session timeout check (returns true if session has expired).
 * Default: 30 minutes for learners, 60 minutes for others.
 */
export function isSessionExpired(
  lastActive: number,
  role: 'learner' | 'parent' | 'teacher' | 'admin' = 'learner',
): boolean {
  const timeoutMs =
    role === 'learner' ? SESSION_TIMEOUT_MS.learner : SESSION_TIMEOUT_MS.default
  return Date.now() - lastActive > timeoutMs
}

/**
 * Check if a username is available (not already taken by another account).
 */
export function isUsernameAvailable(
  accounts: AccountsState,
  username: string,
  excludeAccountId?: string,
): boolean {
  const normalizedUsername = username.toLowerCase().trim()
  if (!normalizedUsername) return false
  return !Object.values(accounts.accountsById).some(
    (a) =>
      a.id !== excludeAccountId &&
      isLearnerAccount(a) &&
      a.username?.toLowerCase() === normalizedUsername,
  )
}

/**
 * Check if an email is available (not already taken by another account).
 */
export function isEmailAvailable(
  accounts: AccountsState,
  email: string,
  excludeAccountId?: string,
): boolean {
  const normalizedEmail = email.toLowerCase().trim()
  if (!normalizedEmail) return false
  return !Object.values(accounts.accountsById).some(
    (a) =>
      a.id !== excludeAccountId && a.email?.toLowerCase() === normalizedEmail,
  )
}

/**
 * Generate a unique 6-digit link code, checking for collisions.
 * Retries up to 10 times to avoid duplicates.
 */
export function generateUniqueLinkCode(accounts: AccountsState): string {
  for (let attempt = 0; attempt < LINK_CODE_MAX_RETRIES; attempt++) {
    const code = generateLinkCode()
    const existing = findAccountByLinkCode(accounts, code)
    if (!existing) return code
  }
  // Fallback: extremely unlikely to reach here
  return generateLinkCode()
}

/**
 * Sanitize a display name (trim, collapse whitespace, limit length).
 */
export function sanitizeDisplayName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').slice(0, DISPLAY_NAME_MAX_LENGTH)
}

/**
 * Sanitize an email address (trim, lowercase).
 */
export function sanitizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

/**
 * Validate registration input comprehensively.
 * Returns null if valid, or an error message string if invalid.
 */
export function validateRegistrationInput(
  role: 'learner' | 'parent' | 'teacher',
  input: {
    name: string
    email?: string
    password: string
    schoolName?: string
  },
  accounts: AccountsState,
): string | null {
  // Name validation
  const sanitizedName = sanitizeDisplayName(input.name)
  if (!sanitizedName || sanitizedName.length < DISPLAY_NAME_MIN_LENGTH) {
    return `Naam moet minimaal ${DISPLAY_NAME_MIN_LENGTH} tekens bevatten.`
  }

  // Password validation
  const minLength =
    role === 'learner'
      ? PASSWORD_MIN_LENGTH.learner
      : PASSWORD_MIN_LENGTH.default
  if (input.password.length < minLength) {
    return `Wachtwoord moet minimaal ${minLength} tekens bevatten.`
  }

  // Email validation for non-learner roles
  if (role !== 'learner') {
    if (!input.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email)) {
      return 'Vul een geldig e-mailadres in.'
    }
    if (!isEmailAvailable(accounts, input.email)) {
      return 'Dit e-mailadres is al in gebruik. Probeer in te loggen of gebruik een ander adres.'
    }
  }

  // Username uniqueness for learners
  if (role === 'learner') {
    const username = suggestUsername(sanitizedName)
    if (!isUsernameAvailable(accounts, username)) {
      return `De gebruikersnaam "${username}" is al in gebruik. Kies een andere naam.`
    }
  }

  // School name for teachers
  if (role === 'teacher') {
    if (!input.schoolName || input.schoolName.trim().length < 2) {
      return 'Vul de naam van je school in.'
    }
  }

  return null
}
